import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PotentialTier, TalentOverviewEntry, TalentOverviewResponse } from '../api/types';
import { describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import { POTENTIAL_TIER_LABEL, POTENTIAL_TIER_OPTIONS } from '../components/profile/attributeDisplay';
import styles from './TalentPage.module.css';

const ANY_FILTER = '';

function matchesQuery(employee: TalentOverviewEntry, query: string): boolean {
  const haystack = [
    employee.fullName,
    employee.currentRoleName,
    employee.currentTeamName,
    employee.currentDepartmentName,
    ...employee.talentDesignations.map((d) => d.label),
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

/** Every distinct, present value for a field — never an invented category
 * or range. A filter option only exists here because at least one visible
 * person currently has that value. */
function distinctValues(employees: readonly TalentOverviewEntry[], pick: (e: TalentOverviewEntry) => string | null): string[] {
  const values = new Set<string>();
  for (const employee of employees) {
    const value = pick(employee);
    if (value) values.add(value);
  }
  return [...values].sort((a, b) => a.localeCompare(b));
}

function distinctLabels(employees: readonly TalentOverviewEntry[]): string[] {
  const labels = new Set<string>();
  for (const employee of employees) {
    for (const designation of employee.talentDesignations) labels.add(designation.label);
  }
  return [...labels].sort((a, b) => a.localeCompare(b));
}

/**
 * Talent — the organisation-wide destination `UX_ARCHITECTURE.md` §2 named
 * but left POST-MVP ("Talent designations, potential/readiness views"),
 * and §7 explicitly distinguished from the per-employee Talent Designation
 * section already on the profile (`TalentDesignationSection.tsx`): "a
 * materially larger surface (cross-employee, not one person's own
 * record)." This is that surface, finally built.
 *
 * ---------------------------------------------------------------------
 * DISCOVER → FILTER → UNDERSTAND → OPEN PROFILE, NOT RANK → SCORE
 *
 * Every row links to the existing Employee Profile
 * (`EmployeeProfilePage.tsx`) — there is no parallel detail view here, and
 * no interaction available beyond opening it. The list is always ordered
 * exactly as `GET /talent-overview` returns it (alphabetically by name,
 * the same order `GET /employees` uses) regardless of which filters are
 * active — filtering narrows *which* rows show, it never reorders them by
 * how much potential or how many designations a person has. There is no
 * "Top Talent" or "Priority" view, and no sortable column, because
 * `docs/MVP_SCOPE.md` §6 rules cross-employee ranking out and this
 * mission's own brief repeats it explicitly.
 *
 * Talent Designation and Potential are rendered as two separate badges,
 * never combined into one value — `docs/PRODUCT_CONSTITUTION.md` §8:
 * "These dimensions must not be collapsed into a single 'talent' score."
 * Readiness does not appear here at all; see `talentOverviewService.ts`
 * (domain) for why.
 *
 * PRIVACY
 *
 * This list can never name anyone the caller could not already open —
 * `GET /talent-overview` uses the identical `resolveVisibleEmployees`
 * scope `GET /employees` does — and every field shown for a person is one
 * already visible on that person's own profile. Nothing here is a
 * backdoor into information the profile itself would withhold.
 * ---------------------------------------------------------------------
 */
export function TalentPage() {
  const state = useApiResource<TalentOverviewResponse>('/talent-overview');
  const [query, setQuery] = useState('');
  const [department, setDepartment] = useState(ANY_FILTER);
  const [team, setTeam] = useState(ANY_FILTER);
  const [label, setLabel] = useState(ANY_FILTER);
  const [potentialTier, setPotentialTier] = useState<PotentialTier | typeof ANY_FILTER>(ANY_FILTER);

  const employees = state.status === 'loaded' ? state.data.employees : [];

  const departments = useMemo(() => distinctValues(employees, (e) => e.currentDepartmentName), [employees]);
  const teams = useMemo(() => distinctValues(employees, (e) => e.currentTeamName), [employees]);
  const labels = useMemo(() => distinctLabels(employees), [employees]);

  const filtersActive = query.trim() !== '' || department !== ANY_FILTER || team !== ANY_FILTER || label !== ANY_FILTER || potentialTier !== ANY_FILTER;

  const filtered = useMemo(() => {
    return employees.filter((employee) => {
      if (query.trim() && !matchesQuery(employee, query)) return false;
      if (department !== ANY_FILTER && employee.currentDepartmentName !== department) return false;
      if (team !== ANY_FILTER && employee.currentTeamName !== team) return false;
      if (label !== ANY_FILTER && !employee.talentDesignations.some((d) => d.label === label)) return false;
      if (potentialTier !== ANY_FILTER && employee.potentialTier !== potentialTier) return false;
      return true;
    });
  }, [employees, query, department, team, label, potentialTier]);

  if (state.status === 'loading') return <LoadingState label="Loading talent…" />;

  if (state.status === 'failed') {
    return <ErrorState title="This view could not be loaded" detail={describeError(state.error)} />;
  }

  const { scope } = state.data;

  function clearFilters() {
    setQuery('');
    setDepartment(ANY_FILTER);
    setTeam(ANY_FILTER);
    setLabel(ANY_FILTER);
    setPotentialTier(ANY_FILTER);
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Talent</h1>
        <p className={styles.scope}>
          {scope === 'organisation'
            ? `Everyone in your organisation · ${employees.length} ${employees.length === 1 ? 'person' : 'people'}`
            : `You and the people you lead · ${employees.length} ${employees.length === 1 ? 'person' : 'people'}`}
        </p>
      </header>

      {employees.length === 0 ? (
        <EmptyState
          title="No people to show"
          detail="Nobody is currently visible to you here. If you expect to see your team, check that they are assigned to a team you lead."
        />
      ) : (
        <>
          <div className={styles.filters}>
            <input
              type="search"
              className={styles.search}
              placeholder="Search by name, role, team, department or designation…"
              aria-label="Search talent"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <label className={styles.filter}>
              <span>Department</span>
              <select value={department} onChange={(event) => setDepartment(event.target.value)}>
                <option value={ANY_FILTER}>Any department</option>
                {departments.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.filter}>
              <span>Team</span>
              <select value={team} onChange={(event) => setTeam(event.target.value)}>
                <option value={ANY_FILTER}>Any team</option>
                {teams.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.filter}>
              <span>Talent designation</span>
              <select value={label} onChange={(event) => setLabel(event.target.value)}>
                <option value={ANY_FILTER}>Any designation</option>
                {labels.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.filter}>
              <span>Potential</span>
              <select value={potentialTier} onChange={(event) => setPotentialTier(event.target.value as PotentialTier | '')}>
                <option value={ANY_FILTER}>Any potential</option>
                {POTENTIAL_TIER_OPTIONS.map((tier) => (
                  <option key={tier} value={tier}>
                    {POTENTIAL_TIER_LABEL[tier]}
                  </option>
                ))}
              </select>
            </label>
            {filtersActive ? (
              <button type="button" className={styles.clear} onClick={clearFilters}>
                Clear filters
              </button>
            ) : null}
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              title="No one matches these filters"
              detail="Nobody visible to you matches the current search and filters. Everyone above is still there — try a different combination, or clear filters."
            />
          ) : (
            <ul className={styles.list}>
              {filtered.map((employee) => (
                <li key={employee.employeeId}>
                  <Link to={`/people/${employee.employeeId}`} className={styles.card}>
                    <span className={styles.name}>{employee.fullName}</span>
                    <span className={styles.role}>
                      {employee.currentRoleName ?? <span className={styles.absent}>No current role</span>}
                    </span>
                    <span className={styles.placement}>
                      {employee.currentTeamName ? (
                        <>
                          {employee.currentTeamName}
                          {employee.currentDepartmentName ? (
                            <span className={styles.department}> · {employee.currentDepartmentName}</span>
                          ) : null}
                        </>
                      ) : (
                        <span className={styles.absent}>Unassigned</span>
                      )}
                    </span>

                    <span className={styles.badges}>
                      {employee.talentDesignations.length === 0 && employee.potentialTier === null ? (
                        <span className={styles.absent}>No talent designation or potential recorded</span>
                      ) : (
                        <>
                          {employee.talentDesignations.map((designation) => (
                            <span key={designation.label} className={styles.badge}>
                              {designation.label}
                              {designation.ambiguous ? (
                                <span
                                  className={styles.badgeFlag}
                                  title="More than one independent active record exists for this designation — see the profile for detail."
                                >
                                  {' '}
                                  ⚠
                                </span>
                              ) : null}
                            </span>
                          ))}
                          {employee.potentialTier ? (
                            <span className={`${styles.badge} ${styles.badgePotential}`}>
                              {`Potential: ${POTENTIAL_TIER_LABEL[employee.potentialTier]}`}
                            </span>
                          ) : null}
                        </>
                      )}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
